import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import type { IShiftsService } from './IShiftsService';
import { ShiftsEndpoints } from './shiftsEndpoints';
import {
    type CloseShiftByIdRequest,
    type CloseShiftRequest,
    type OpenShiftRequest,
} from './requests';
import type { GetShiftRegisterQuery } from './queries';
import {
    type ShiftExpectedResponse,
    type ShiftLedgerSummaryResponse,
    type ShiftRegisterResponse,
    type ShiftResponse,
    type ShiftStatusResponse,
} from './responses';

type ShiftsServiceOptions = {
    baseUrl: string;
};

/**
 * Transport for the cashier shift lifecycle (bos-api BOS-73). Seller-token
 * endpoints — the request interceptor attaches the access token by default, so
 * no `authPaths` entry is needed. Money is server-authoritative: the client
 * sends only the counted cash (+ a co-sign on a discrepancy), never the expected
 * amount.
 */
export class ShiftsService implements IShiftsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: ShiftsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public async getCurrent(): Promise<ApiResponse<ShiftResponse | null>> {
        const url = ShiftsEndpoints.current();
        const result = await makeApiRequest<ShiftResponse | null>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
        // A 204 (no open shift) comes back as a success with an empty body —
        // normalise that to an explicit `null` so consumers read "closed".
        if (result.success && !result.response) {
            return { success: true, response: null };
        }
        return result;
    }

    public getExpected(
        type: 'open' | 'close',
    ): Promise<ApiResponse<ShiftExpectedResponse>> {
        // axios-case-converter rewrites the camelCase `type` to PascalCase on
        // the wire; ASP.NET binds it case-insensitively to `type`.
        const url = ShiftsEndpoints.expected();
        return makeApiRequest<ShiftExpectedResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: { type },
            }),
        );
    }

    public getStatus(
        shiftId: string,
    ): Promise<ApiResponse<ShiftStatusResponse>> {
        // Not narrowed to the caller: the device asks about a shift another
        // seller may have opened. The server narrows it to the caller's branch.
        const url = ShiftsEndpoints.status(shiftId);
        return makeApiRequest<ShiftStatusResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public open(body: OpenShiftRequest): Promise<ApiResponse<ShiftResponse>> {
        const url = ShiftsEndpoints.open();
        return makeApiRequest<ShiftResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public close(body: CloseShiftRequest): Promise<ApiResponse<ShiftResponse>> {
        const url = ShiftsEndpoints.close();
        return makeApiRequest<ShiftResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public closeById(
        shiftId: string,
        body: CloseShiftByIdRequest,
    ): Promise<ApiResponse<ShiftResponse>> {
        // An admin write (bos-api `ShiftManage`): the server narrows the shift
        // to the caller's branch and records the admin as closer and approver.
        const url = ShiftsEndpoints.closeById(shiftId);
        return makeApiRequest<ShiftResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public getRegister(
        query: GetShiftRegisterQuery,
    ): Promise<ApiResponse<ShiftRegisterResponse>> {
        // An admin read (bos-api `ShiftRead`): the server narrows the list to
        // the caller's branch from the token.
        const url = ShiftsEndpoints.register();
        return makeApiRequest<ShiftRegisterResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: query,
            }),
        );
    }

    public getLedgerSummary(
        shiftId: string,
        locale: string,
    ): Promise<ApiResponse<ShiftLedgerSummaryResponse>> {
        // `locale` picks the account-name translation the server returns.
        const url = ShiftsEndpoints.ledgerSummary(shiftId);
        return makeApiRequest<ShiftLedgerSummaryResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: { locale },
            }),
        );
    }
}
